import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import {
  searchFile,
  searchFileContent,
  SearchFileRequest,
  SearchFileContentRequest
} from "src/network/graphql/bulkUploadService";
import {
  BulkUploadFile,
  BulkUploadFileContent,
  BulkUploadFileContentListing,
  BulkUploadFileListing,
  parseFileContentAttributes,
  parseFileContentContent
} from "./bulkUploadTypes";

interface BulkUploadState {
  fileListLoading: boolean;
  fileListError: string | null;
  files: BulkUploadFileListing;

  detailFileLoading: boolean;
  detailFileError: string | null;
  detailFile: BulkUploadFile | null;

  contentListLoading: boolean;
  contentListError: string | null;
  contents: BulkUploadFileContentListing;
}

const initialState: BulkUploadState = {
  fileListLoading: false,
  fileListError: null,
  files: { list: [], total: 0 },

  detailFileLoading: false,
  detailFileError: null,
  detailFile: null,

  contentListLoading: false,
  contentListError: null,
  contents: { list: [], total: 0 }
};

const bulkUploadSlice = createSlice({
  name: "bulkUpload",
  initialState,
  reducers: {
    fetchFilesStart: (state) => {
      state.fileListLoading = true;
      state.fileListError = null;
    },
    fetchFilesSuccess: (state, action: PayloadAction<BulkUploadFileListing>) => {
      state.fileListLoading = false;
      state.files = action.payload;
    },
    fetchFilesFail: (state, action: PayloadAction<string>) => {
      state.fileListLoading = false;
      state.fileListError = action.payload;
      state.files = { list: [], total: 0 };
    },
    fetchDetailFileStart: (state) => {
      state.detailFileLoading = true;
      state.detailFileError = null;
    },
    fetchDetailFileSuccess: (state, action: PayloadAction<BulkUploadFile | null>) => {
      state.detailFileLoading = false;
      state.detailFile = action.payload;
    },
    fetchDetailFileFail: (state, action: PayloadAction<string>) => {
      state.detailFileLoading = false;
      state.detailFileError = action.payload;
      state.detailFile = null;
    },
    fetchContentsStart: (state) => {
      state.contentListLoading = true;
      state.contentListError = null;
    },
    fetchContentsSuccess: (state, action: PayloadAction<BulkUploadFileContentListing>) => {
      state.contentListLoading = false;
      state.contents = action.payload;
    },
    fetchContentsFail: (state, action: PayloadAction<string>) => {
      state.contentListLoading = false;
      state.contentListError = action.payload;
      state.contents = { list: [], total: 0 };
    },
    resetBulkUploadDetail: (state) => {
      state.detailFile = null;
      state.detailFileError = null;
      state.contents = { list: [], total: 0 };
      state.contentListError = null;
    }
  }
});

export const {
  fetchFilesStart,
  fetchFilesSuccess,
  fetchFilesFail,
  fetchDetailFileStart,
  fetchDetailFileSuccess,
  fetchDetailFileFail,
  fetchContentsStart,
  fetchContentsSuccess,
  fetchContentsFail,
  resetBulkUploadDetail
} = bulkUploadSlice.actions;

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Something went wrong";
};

export const fetchBulkUploadFilesAction = (data: SearchFileRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchFilesStart());
    try {
      const response = await searchFile(data);
      const payload = response.data?.search_file ?? {};
      const files: BulkUploadFile[] = payload.files ?? [];

      dispatch(
        fetchFilesSuccess({
          list: files,
          total: payload.total_size ?? files.length
        })
      );
    } catch (error) {
      dispatch(fetchFilesFail(toErrorMessage(error)));
    }
  };
};

export const fetchBulkUploadDetailFileAction = (fileId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDetailFileStart());
    try {
      const response = await searchFile({
        from: 0,
        size: 1,
        filter: { file_ids: [fileId] }
      });
      const file: BulkUploadFile | undefined = response.data?.search_file?.files?.[0];

      dispatch(fetchDetailFileSuccess(file ?? null));
    } catch (error) {
      dispatch(fetchDetailFileFail(toErrorMessage(error)));
    }
  };
};

export const fetchBulkUploadContentsAction = (data: SearchFileContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchContentsStart());
    try {
      const response = await searchFileContent(data);
      const payload = response.data?.search_file_content ?? {};
      const rawList: BulkUploadFileContent[] = payload.file_content_list ?? [];

      const normalised = rawList.map((row) => ({
        ...row,
        attributes: parseFileContentAttributes(row.attributes),
        content: parseFileContentContent(row.content)
      }));

      dispatch(
        fetchContentsSuccess({
          list: normalised,
          total: payload.total_size ?? normalised.length
        })
      );
    } catch (error) {
      dispatch(fetchContentsFail(toErrorMessage(error)));
    }
  };
};

export default bulkUploadSlice.reducer;
